import React from "react";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Copy, Search, BarChart, User, FolderOpen } from "lucide-react";

const HowToUse = () => {
  return (
    <Layout>
      <div className="bg-lawyer-light py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold font-display mb-4 text-center text-lawyer-primary">Como Usar o LexPrompt</h1>
            <p className="text-gray-600 text-center mb-12 text-lg">
              Um guia passo a passo para aproveitar ao máximo nossa plataforma de prompts para advogados.
            </p>
            
            <div className="mb-16">
              <h2 className="text-2xl font-semibold font-display mb-6 text-lawyer-primary">Começando</h2>
              
              <div className="space-y-8">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex items-center justify-center bg-white rounded-full w-12 h-12 shadow-md flex-shrink-0 text-lawyer-secondary">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Crie sua conta</h3>
                    <p className="text-gray-600 mb-3">
                      Clique em "Registrar" no topo da página e crie sua conta utilizando seu e-mail. O processo leva apenas alguns segundos.
                    </p>
                    <Card className="border border-gray-200">
                      <CardContent className="p-4 flex items-center bg-gray-50">
                        <div className="mr-4">
                          <User className="h-6 w-6 text-lawyer-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">Preencha seus dados básicos e escolha uma senha segura. Você receberá um e-mail de confirmação para validar sua conta.</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row gap-6 items-start hidden">
                  <div className="flex items-center justify-center bg-white rounded-full w-12 h-12 shadow-md flex-shrink-0 text-lawyer-secondary">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Assine o plano</h3>
                    <p className="text-gray-600 mb-3">
                      Após criar sua conta, você será direcionado para escolher seu plano. Oferecemos apenas uma opção simples: R$ 0,00/mês com acesso completo.
                    </p>
                    <Card className="border border-gray-200">
                      <CardContent className="p-4 flex items-center bg-gray-50">
                        <div className="mr-4">
                          <ArrowRight className="h-6 w-6 text-lawyer-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">Ao clicar em "Assinar agora", você será redirecionado para nossa plataforma segura de pagamento (Stripe) para concluir sua assinatura.</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex items-center justify-center bg-white rounded-full w-12 h-12 shadow-md flex-shrink-0 text-lawyer-secondary">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Acesse o dashboard</h3>
                    <p className="text-gray-600 mb-3">
                      Após a confirmação do pagamento, você terá acesso imediato ao seu dashboard personalizado com todos os prompts disponíveis organizados por áreas do Direito.
                    </p>
                    <Card className="border border-gray-200">
                      <CardContent className="p-4 flex items-center bg-gray-50">
                        <div className="mr-4">
                          <FolderOpen className="h-6 w-6 text-lawyer-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">No dashboard, você encontrará todos os prompts organizados por categorias como Trabalhista, Cível, Contratual, Tributário, etc.</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-16">
              <h2 className="text-2xl font-semibold font-display mb-6 text-lawyer-primary">Utilizando os Prompts</h2>
              
              <Tabs defaultValue="encontrar" className="w-full">
                <TabsList className="grid grid-cols-3 mb-8">
                  <TabsTrigger value="encontrar">Encontrar Prompts</TabsTrigger>
                  <TabsTrigger value="personalizar">Personalizar</TabsTrigger>
                  <TabsTrigger value="utilizar">Utilizar na IA</TabsTrigger>
                </TabsList>
                
                <TabsContent value="encontrar">
                  <Card className="border border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex items-start mb-4">
                        <Search className="h-6 w-6 text-lawyer-secondary mr-4 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-lg font-medium mb-2">Encontrando o prompt ideal</h4>
                          <p className="text-gray-600 mb-4">
                            Você pode encontrar prompts de três maneiras principais:
                          </p>
                          <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
                            <li><strong>Navegação por Categorias:</strong> Use o menu lateral para navegar entre diferentes áreas do Direito.</li>
                            <li><strong>Pesquisa:</strong> Utilize a barra de pesquisa no topo para encontrar prompts específicos por palavras-chave.</li>
                            <li><strong>Favoritos:</strong> Marque prompts como favoritos para acessá-los rapidamente na próxima visita.</li>
                          </ul>
                          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                            <p className="text-sm text-gray-700">
                              <strong>Dica:</strong> Na página inicial do dashboard, você sempre verá seus prompts mais utilizados e as novidades adicionadas desde seu último acesso.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="personalizar">
                  <Card className="border border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex items-start mb-4">
                        <User className="h-6 w-6 text-lawyer-secondary mr-4 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-lg font-medium mb-2">Personalizando prompts para seu caso</h4>
                          <p className="text-gray-600 mb-4">
                            Cada prompt contém instruções detalhadas e campos para personalização:
                          </p>
                          <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
                            <li>Identifique todos os campos entre [COLCHETES] que precisam ser substituídos.</li>
                            <li>Preencha cada campo com as informações específicas do seu caso ou cliente.</li>
                            <li>Avalie se todas as seções do prompt são relevantes para sua situação específica.</li>
                            <li>Se necessário, adicione instruções adicionais ao final do prompt.</li>
                          </ul>
                          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                            <p className="text-sm text-gray-700">
                              <strong>Exemplo:</strong> No prompt para "Contestação Cível", substitua [TIPO DA AÇÃO] por "Ação de Cobrança", [NÚMERO] por "0001234-55.2026.8.26.0100", e assim por diante.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="utilizar">
                  <Card className="border border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex items-start mb-4">
                        <Copy className="h-6 w-6 text-lawyer-secondary mr-4 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-lg font-medium mb-2">Utilizando com ferramentas de IA</h4>
                          <p className="text-gray-600 mb-4">
                            Após personalizar seu prompt, siga estes passos:
                          </p>
                          <ol className="list-decimal pl-6 space-y-2 text-gray-600 mb-6">
                            <li>Clique no botão "Copiar Prompt" para copiar o texto completo para a área de transferência.</li>
                            <li>Abra sua ferramenta de IA preferida (ChatGPT, Claude, Gemini, etc.).</li>
                            <li>Cole o prompt completo em uma nova conversa.</li>
                            <li>Envie a mensagem e aguarde a resposta da IA.</li>
                            <li>Revise, edite e adapte o resultado conforme necessário.</li>
                          </ol>
                          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                            <p className="text-sm text-gray-700">
                              <strong>Importante:</strong> Sempre revise criticamente o resultado gerado pela IA. Nossos prompts são otimizados para bons resultados, mas você, como profissional jurídico, deve avaliar a precisão e adequação do conteúdo ao seu caso específico.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="mb-16">
              <h2 className="text-2xl font-semibold font-display mb-6 text-lawyer-primary">Recursos Adicionais</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-gray-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start mb-2">
                      <BarChart className="h-6 w-6 text-lawyer-secondary mr-3 flex-shrink-0" />
                      <h4 className="text-lg font-medium">Estatísticas de Uso</h4>
                    </div>
                    <p className="text-gray-600 mb-4">
                      Acesse seu histórico completo de uso, visualize quais prompts você utiliza com mais frequência e acompanhe seu progresso ao longo do tempo.
                    </p>
                    <p className="text-sm text-gray-500">
                      Disponível na seção "Estatísticas" do seu dashboard.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border border-gray-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start mb-2">
                      <User className="h-6 w-6 text-lawyer-secondary mr-3 flex-shrink-0" />
                      <h4 className="text-lg font-medium">Perfil e Configurações</h4>
                    </div>
                    <p className="text-gray-600 mb-4">
                      Gerencie suas informações pessoais, acompanhe o status da sua assinatura e acesse as opções para cancelamento ou alteração do plano.
                    </p>
                    <p className="text-sm text-gray-500">
                      Disponível clicando no seu nome no canto superior direito do dashboard.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="text-center mt-12 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-lawyer-primary">Ainda tem dúvidas?</h3>
              <p className="text-gray-600 mb-6">
                Nossa equipe está disponível para ajudar com quaisquer questões ou dificuldades que você possa encontrar.
              </p>
              <a 
                href="mailto:contato@decidedigital.com.br" 
                className="inline-block bg-lawyer-primary hover:bg-lawyer-accent text-white font-medium py-2 px-6 rounded-md transition-colors"
              >
                Entre em Contato com o Suporte
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HowToUse;
